import { Button } from "@bokio/designsystem/components/Button";
import { Modal, ModalContent, ModalFooter } from "@bokio/designsystem/components/Modal";
import Markdown from "@bokio/elements/Markdown/Markdown";
import BankLangFactory from "@bokio/lang/BankLangFactory";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { getRoute } from "@bokio/shared/route";
import { noop } from "@bokio/shared/utils";
import { formatMessage } from "@bokio/shared/utils/format";

import type { RequestState } from "@bokio/mobile-web-shared/services/api/requestState";

import SupportMessageType = m.Entities.Support.SupportMessageType;

export interface BbaInactivityFeeModalProps {
	companyId: string;
	type: m.Entities.Support.SupportMessageType;
	resolve: () => void;
	resolveRequest: RequestState<m.Envelope<boolean, m.SimpleError>>;
	dismiss: () => void;
	dismissRequest: RequestState<m.Envelope<boolean, m.SimpleError>>;
}

export const BbaInactivityFeeModal = (props: BbaInactivityFeeModalProps) => {
	const bankLang = BankLangFactory();
	const { companyInfo } = useCompanyInfo();

	const getDetailsForModal = () => {
		switch (props.type) {
			case SupportMessageType.BbaInactivityFee:
				return {
					title: bankLang.BbaInactivityFee_SupportMessage_Title,
					bodyContent: formatMessage(bankLang.BbaInactivityFee_SupportMessage, companyInfo.Name),
					button: (
						<Button
							appearance="secondary"
							disabled={props.resolveRequest.isLoading}
							loading={props.resolveRequest.isLoading}
							type="button"
							onClick={props.resolve}
						>
							{bankLang.BbaInactivityFee_SupportMessage_CloseButton}
						</Button>
					),
				};
			case SupportMessageType.BbaNonConsentInactivityFee:
				return {
					title: bankLang.BbaInactivityFee_SupportMessage_Title,
					bodyContent: formatMessage(bankLang.BbaInactivityFee_NonConsent_SupportMessage, companyInfo.Name),
					button: (
						<Button
							appearance="secondary"
							disabled={props.dismissRequest.isLoading}
							loading={props.dismissRequest.isLoading}
							type="link"
							route={getRoute("sveaBankConsent", { company: props.companyId })}
							onNavigation={props.resolve}
						>
							{bankLang.BbaInactivityFee_NonConsent_SupportMesaage_Button}
						</Button>
					),
				};
			default:
				return {
					title: "",
					bodyContent: "",
					button: null,
				};
		}
	};

	const { title, bodyContent, button } = getDetailsForModal();

	if (!bodyContent || !button || !title) {
		return null;
	}

	return (
		<>
			<Modal
				visible={true}
				disableBackgroundClick={true}
				testId="BbaInactivityFee_modal"
				onClose={noop} // Not closable
				title={title}
				disabled={true} // disables close button
			>
				<ModalContent>
					<Markdown markdownContent={bodyContent} deprecatedOptoutFromTypographicElements />
				</ModalContent>

				<ModalFooter>
					{button}
					<Button
						disabled={props.dismissRequest.isLoading}
						loading={props.dismissRequest.isLoading}
						type="link"
						route={getRoute("bokioBusinessAccountOffboarding", { company: props.companyId })}
						onNavigation={props.dismiss}
						testId="BbaInactivityFee_OffboardButton"
					>
						{bankLang.BbaInactivityFee_SupportMesaage_TerminateBbaButton}
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};
